import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { MyBookingDetails, MyBookingErrorDetails, MyBookingToken } from '@model/mmb';
import { MyBookingLoginFormPayload } from '@model/forms';
import { HYDRATE } from 'next-redux-wrapper';
import { isEqual } from 'lodash';
import { Page } from '@model/common';
import { MyBookingApi } from '@model/iceberg/service/mmb/my-booking-api';
import { log } from '@util/logging';
import { LogType } from '@model/common/logging';
import { performNavigate, performRedirect } from '@state/slices/app';
import { resetPaymentForm } from '@state/slices/payment/paymentFormSlice';
import { logoutAgent } from '@state/slices/agent';

/* ***************** *
 *       TYPES       *
 * ***************** */

export interface MyBookingState {
  bookingDetails: MyBookingDetails;
  loading: boolean;
  error: boolean;
  locked: boolean;
  agent: boolean;
  token: MyBookingToken;
  bookingId: string;
  bookingError?: MyBookingErrorDetails;
  pending: boolean;
}

export interface MyBookingDetailsRequestPayload {
  loginToken: string;
  bookingId: number;
}

export interface MyBookingDetailsPollPayload extends MyBookingDetailsRequestPayload {
  retryIf: (bookingDetails: any) => boolean;
}

export interface MyBookingLogoutPayload {
  bookingId: number;
}

export interface MyBookingPaymentPayload {
  cardholderName: string;
  postCode: string;
  amount: string;
}

/* ***************** *
 *   ACTION TYPES    *
 * ***************** */

export enum MyBookingActions {
  SLICE_NAME = '@MMB',
  REQUEST_BOOKING_TOKEN = '@MMB/REQUEST_BOOKING_TOKEN',
  REQUEST_BOOKING_LOGOUT = '@MMB/REQUEST_BOOKING_LOGOUT',
  REQUEST_BOOKING_DETAILS = '@MMB/REQUEST_BOOKING_DETAILS',
  POLL_BOOKING_DETAILS = '@MMB/POLL_BOOKING_DETAILS',
  RESET_BOOKING_DETAILS = '@MMB/resetBookingDetails',
  RESET_BOOKING_PENDING = '@MMB/resetBookingPending',
  RESET_BOOKING_ERROR = '@MMB/resetBookingError'
}

/* ***************** *
 *   ASYNC THUNKS    *
 * ***************** */

/**
 * Async thunk for fetching booking token
 * Replaces the saga-based token fetching logic
 */
export const fetchBookingToken = createAsyncThunk(
  MyBookingActions.REQUEST_BOOKING_TOKEN,
  async (payload: MyBookingLoginFormPayload, { dispatch, rejectWithValue }) => {
    try {
      const api = new MyBookingApi();
      const response = await api.getMyBookingToken(payload);

      // Handle successful response - dispatch additional actions
      dispatch(performRedirect(Page.MY_BOOKING_OVERVIEW));
      dispatch(resetPaymentForm());

      return response.data;
    } catch (error: any) {
      // Log the error for debugging
      log({
        statusCode: error.response?.status,
        text: `Error fetching MMB Booking Token: ${error.errorText}`,
        logType: LogType.ERROR
      });

      // Return specific error info for different status codes
      return rejectWithValue({
        status: error.response?.status,
        errorText: error.errorText,
        isLocked: error.response?.status === 429,
        isAgent: error.response?.status === 403
      });
    }
  }
);

/**
 * Async thunk for fetching booking details
 * Handles both success and pending states based on error codes
 */
export const fetchBookingDetails = createAsyncThunk(
  MyBookingActions.REQUEST_BOOKING_DETAILS,
  async (payload: MyBookingDetailsRequestPayload, { rejectWithValue }) => {
    try {
      const api = new MyBookingApi();
      const response = await api.getMyBookingDetails(payload);
      return response.data;
    } catch (error: any) {
      log({
        statusCode: error?.errorCode || error.response?.status,
        text: `Error fetching MMB Booking Details: ${error.errorText || error.toString()}`,
        logType: LogType.ERROR
      });

      if (error?.errorCode === 202) {
        return rejectWithValue({
          isPending: true,
          errorCode: error.errorCode,
          errorText: error.errorText
        });
      }

      return rejectWithValue({
        isPending: false,
        status: error.response?.status,
        data: error.response?.data,
        errorText: error.errorText
      });
    }
  }
);

/**
 * Async thunk for polling booking details
 * Used when booking details need to be refreshed periodically
 */
export const updateBookingDetails = createAsyncThunk(
  MyBookingActions.POLL_BOOKING_DETAILS,
  async (payload: MyBookingDetailsPollPayload, { rejectWithValue }) => {
    try {
      const api = new MyBookingApi();
      const response = await api.pollMyBookingDetails(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        errorText: error.errorText
      });
    }
  }
);

/**
 * Async thunk for booking logout
 * Handles token invalidation and navigation
 */
export const logoutBooking = createAsyncThunk(
  MyBookingActions.REQUEST_BOOKING_LOGOUT,
  async (payload: MyBookingLogoutPayload, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as { mmb: MyBookingState };
      const token = state.mmb.token;

      const api = new MyBookingApi();
      const response = await api.invalidateBookingLogin(payload.bookingId, token.loginToken);

      if (response.loggedOut) {
        dispatch(performNavigate(Page.MY_BOOKING));
        dispatch(resetPaymentForm());
      }

      return response;
    } catch (error: any) {
      log({
        statusCode: error.response?.status,
        text: `Error invalidating MBB token: ${error.errorText}`,
        logType: LogType.ERROR
      });

      return rejectWithValue({
        errorText: error.errorText
      });
    }
  }
);

/* ***************** *
 *      SLICE        *
 * ***************** */

export const MMB_INITIAL_STATE: MyBookingState = {
  bookingDetails: {} as any,
  loading: false,
  error: false,
  pending: false,
  locked: false,
  agent: false,
  token: {} as any,
  bookingId: ''
};

const mmbSlice = createSlice({
  name: MyBookingActions.SLICE_NAME,
  initialState: MMB_INITIAL_STATE,
  reducers: {
    setBookingID: (state, action: PayloadAction<string>) => {
      state.bookingId = action.payload;
    },

    resetBookingDetails: (state) => {
      const currentBookingError = state.bookingError;
      const currentPending = state.pending;

      Object.assign(state, MMB_INITIAL_STATE);

      if (currentBookingError) {
        state.bookingError = currentBookingError;
      }
      if (currentPending) {
        state.pending = currentPending;
      }
    },

    resetBookingPending: (state) => {
      state.pending = false;
    },

    resetBookingError: (state) => {
      state.bookingError = undefined;
    },

    requestBookingDetailsSuccess: (state, action) => {
      state.bookingDetails = action.payload;
      state.loading = false;
      state.error = false;
      state.pending = false;
    }
  },

  extraReducers: (builder) => {
    builder.addCase(HYDRATE as any, (state, action: any) => {
      if (isEqual(state, MMB_INITIAL_STATE)) {
        return action.payload?.mmb || state;
      }
      return state;
    });

    builder
      .addCase(fetchBookingToken.pending, (state) => {
        state.loading = true;
        state.error = false;
        state.pending = false;
        state.locked = false;
        state.agent = false;
        state.bookingDetails = {} as any;
      })
      .addCase(fetchBookingToken.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload;
        state.error = false;
      })
      .addCase(fetchBookingToken.rejected, (state, action: any) => {
        state.loading = false;

        if (action.payload?.isLocked) {
          Object.assign(state, MMB_INITIAL_STATE);
          state.locked = true;
        } else if (action.payload?.isAgent) {
          Object.assign(state, MMB_INITIAL_STATE);
          state.agent = true;
        } else {
          Object.assign(state, MMB_INITIAL_STATE);
          state.error = true;
        }
      });

    // Handle fetchBookingDetails async thunk
    builder
      .addCase(fetchBookingDetails.pending, (state) => {
        state.loading = true;
        state.error = false;
        state.pending = false;
        state.bookingDetails = {} as any;
      })
      .addCase(fetchBookingDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.bookingDetails = action.payload;
        state.error = false;
        state.pending = false;
      })
      .addCase(fetchBookingDetails.rejected, (state, action: any) => {
        state.loading = false;

        if (action.payload?.isPending) {
          Object.assign(state, MMB_INITIAL_STATE);
          state.error = false;
          state.pending = true;
          // @ts-ignore
          state.bookingPendingError = {
            errorCode: action.payload.errorCode,
            errorText: action.payload.errorText
          };
        } else {
          Object.assign(state, MMB_INITIAL_STATE);
          state.error = true;
          state.bookingError = {
            status: action.payload?.status,
            data: action.payload?.data
          };
        }
      });

    // Handle updateBookingDetails async thunk
    builder.addCase(updateBookingDetails.fulfilled, (state, action) => {
      state.bookingDetails = action.payload;
      state.loading = false;
      state.error = false;
      state.pending = false;
    });

    // Handle logoutBooking async thunk
    builder.addCase(logoutBooking.fulfilled, (state) => {
      Object.assign(state, MMB_INITIAL_STATE);
    });

    // Handle logoutAgent fulfilled
    builder.addCase(logoutAgent.fulfilled, () => MMB_INITIAL_STATE);
  }
});

export const {
  setBookingID,
  resetBookingDetails,
  resetBookingPending,
  resetBookingError,
  requestBookingDetailsSuccess
} = mmbSlice.actions;

export const mmbReducer = mmbSlice.reducer;
